import { queryOptions } from '@tanstack/react-query'
import {
  type CalendarFilters,
  type MatchRead,
  getMatch,
  isNotFound,
  listClubMatches,
  listGroupMatches,
  listGroupStandings,
  listTeamMatches,
} from '@volee/api-client'

import { api } from '../services/api.ts'
import type { MatchExtras } from './mock/fixtures.ts'
import { mockReader } from './mock/index.ts'

// The queries of the leagues and match pages: all the API answers, plus `matchExtras`, which has no endpoint and is
// marked by the pages. See apps/frontend/docs/campionati-e-partite.md § «Le due aree su dati finti»

export const MATCHES_KEY = ['matches'] as const

export function matchDetailKey(matchId: number) {
  return [...MATCHES_KEY, 'detail', matchId] as const
}

export function groupStandingsKey(groupId: number) {
  return [...MATCHES_KEY, 'standings', groupId] as const
}

export function clubMatchesKey(clubId: number, filters: CalendarFilters) {
  return [...MATCHES_KEY, 'club', clubId, filters] as const
}

/** The lower bound of «upcoming», floored to the day — for two reasons that agree. A
 *  fresh instant would change the query key on every render and refetch forever, and a
 *  fixture kicking off at 15:00 must not vanish from the list at 15:01. */
export function startOfToday(): string {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString()
}

export function groupMatchesKey(groupId: number) {
  return [...MATCHES_KEY, 'group', groupId] as const
}

export function teamMatchesKey(teamId: number, filters: CalendarFilters = {}) {
  const key = [...MATCHES_KEY, 'team', teamId] as const
  return Object.keys(filters).length === 0 ? key : ([...key, filters] as const)
}

/** «This fixture does not exist» as opposed to «something broke», for the four pages that
 *  read a match. `unknown` is what TanStack Query hands a page, the app's one exception. */
export function isMissingMatch(error: unknown): boolean {
  return isNotFound(error)
}

export interface MatchWithExtras {
  readonly match: MatchRead
  readonly extras: MatchExtras
}

/** One document, one cache entry: all four leagues and match pages read a match through here, even the one
 *  that renders no extras — apps/frontend/docs/campionati-e-partite.md § «Le due aree su dati finti».
 *  `Promise.all` because the two halves are independent. */
export function matchWithExtrasQuery(matchId: number) {
  return queryOptions({
    queryKey: matchDetailKey(matchId),
    queryFn: async (): Promise<MatchWithExtras> => {
      const [match, extras] = await Promise.all([
        getMatch(api, matchId),
        mockReader.matchExtras(matchId),
      ])
      return { match, extras }
    },
  })
}

export function groupStandingsQuery(groupId: number) {
  return queryOptions({
    queryKey: groupStandingsKey(groupId),
    queryFn: async () => (await listGroupStandings(api, groupId)).items,
  })
}

export function clubMatchesQuery(clubId: number, filters: CalendarFilters = {}) {
  return queryOptions({
    queryKey: clubMatchesKey(clubId, filters),
    queryFn: async () => (await listClubMatches(api, clubId, filters)).items,
  })
}

export function groupMatchesQuery(groupId: number) {
  return queryOptions({
    queryKey: groupMatchesKey(groupId),
    queryFn: async () => (await listGroupMatches(api, groupId)).items,
  })
}

export function teamMatchesQuery(teamId: number, filters: CalendarFilters = {}) {
  return queryOptions({
    queryKey: teamMatchesKey(teamId, filters),
    queryFn: async () => (await listTeamMatches(api, teamId, filters)).items,
  })
}
